// src/components/UserMenu.js - 多语言版本
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../store/userSlice';
import { updateUserPassword } from '../api/user';
import styles from '../styles/UserMenu.module.css';

const UserMenu = () => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    setIsDropdownOpen(false);
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setIsDropdownOpen(false);
    setPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
  };

  const closeResultModal = () => {
    setShowResultModal(false);
    if (isSuccess) {
      setShowPasswordModal(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const savePassword = async () => {
    setPasswordError('');
    
    if (password.length < 6) {
      setPasswordError(t('userMenu.passwordModal.errors.minLength'));
      return;
    }
    
    if (password !== confirmPassword) {
      setPasswordError(t('userMenu.passwordModal.errors.mismatch'));
      return;
    }
    
    try {
      setIsUpdating(true);
      
      const phoneNumbers = user?.uid || user?.phoneNumbers;
      
      const response = await updateUserPassword({ phoneNumbers, password });
      
      if (response.code === 1) {
        setIsSuccess(true);
        setResultMessage(t('userMenu.passwordModal.success'));
      } else {
        setIsSuccess(false);
        setResultMessage(response.message || t('userMenu.passwordModal.errors.updateFailed'));
      }
      
      setShowResultModal(true);
      
    } catch (error) {
      setIsSuccess(false);
      setResultMessage(t('userMenu.passwordModal.errors.systemError'));
      setShowResultModal(true);
      console.error('更新密码错误:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const displayName = user?.name || user?.uid?.split('@')[0] || t('userMenu.defaultName');
  const displayEmail = user?.uid || 'user@example.com';
  const diamondCount = user?.diamond_count || 0;

  return (
    <div className={styles.userMenu}>
      <div className={styles.userBtn} onClick={toggleDropdown}>
        <div className={styles.userAvatar}>{displayName[0]}</div>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{displayName}</span>
          <span className={styles.userEmail}>{displayEmail}</span>
        </div>
        <i className={`fas fa-chevron-down ${styles.dropdownIcon}`} />
      </div>
      <div className={`${styles.userDropdown} ${isDropdownOpen ? styles.active : ''}`}>
        <div className={styles.dropdownMenu}>
          <Link to="/cart" className={styles.dropdownLink}>
            <i className="fas fa-shopping-bag"></i>
            <span>{t('userMenu.purchaseHistory')}</span>
          </Link>

          <Link to="/account" className={styles.dropdownLink}>
            <i className="fas fa-shopping-bag"></i>
            <span>{t('userMenu.afterSales')}</span>
          </Link>

          <div className={styles.dropdownLink} onClick={openPasswordModal}>
            <i className="fas fa-key"></i>
            <span>{t('userMenu.changePassword')}</span>
          </div>

          <div className={styles.dropdownLink}>
            <i className="fas fa-gem"></i>
            <span>{t('userMenu.coins', { count: diamondCount })}</span>
          </div>

          <div className={styles.dropdownDivider}></div>
          <div className={styles.dropdownLink} onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>{t('userMenu.logout')}</span>
          </div>
        </div>
      </div>

      {/* 修改密码的模态窗口 */}
      {showPasswordModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>{t('userMenu.passwordModal.title')}</h3>
              <button className={styles.closeBtn} onClick={closePasswordModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className={styles.modalBody}>
              {passwordError && (
                <div className={styles.errorMessage}>{passwordError}</div>
              )}
              
              <div className={styles.formGroup}>
                <label htmlFor="password">{t('userMenu.passwordModal.newPassword')}</label>
                <div className={styles.passwordInputContainer}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    className={styles.formControl}
                  />
                  <button 
                    type="button" 
                    className={styles.visibilityToggle}
                    onClick={togglePasswordVisibility}
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">{t('userMenu.passwordModal.confirmPassword')}</label>
                <div className={styles.passwordInputContainer}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    className={styles.formControl}
                  />
                  <button 
                    type="button" 
                    className={styles.visibilityToggle}
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    <i className={`fas ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closePasswordModal}>
                {t('userMenu.passwordModal.buttons.cancel')}
              </button>
              <button 
                className={styles.saveBtn} 
                onClick={savePassword}
                disabled={isUpdating}
              >
                {isUpdating ? t('userMenu.passwordModal.buttons.updating') : t('userMenu.passwordModal.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 结果提示模态窗口 */}
      {showResultModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3>{isSuccess ? t('userMenu.resultModal.success') : t('userMenu.resultModal.error')}</h3>
              <button className={styles.closeBtn} onClick={closeResultModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={isSuccess ? styles.successMessage : styles.errorMessage}>
                {resultMessage}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.saveBtn} onClick={closeResultModal}>
                {t('userMenu.resultModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;